<!-- eslint-disable max-len -->
<template>
  <div class="confirmation-options">
    <div v-if="source === 'imobiliaria'" class="confirmation-header hide-on-large">
      <asset-v3 dir="logos" name="logo.svg" />

      <p class="primary body-regular" @click="() => $emit('detailsClick')">
        Detalhes
      </p>
    </div>

    <div v-if="['WAITING', 'CONFIRMATION'].includes(step) && !showResendOptions" class="alert-success">
      <div class="alert-success-icon">
        <CheckCircleIcon size="20" color="var(--green-600)" />
      </div>
      <div class="alert-success-text" v-if="!chosenConfirmationResendType">
        <h3>Pedido realizado</h3>
        <p>Enviamos um email com os detalhes</p>
      </div>
      <div v-else>
        <h3>
          Código enviado
        </h3>
      </div>
    </div>

    <asset-v3 class="logo" v-else-if="step !== 'COMPLETE'" dir="logos" name="logo.svg" />

    <OnboardingConfirmation v-if="['WAITING', 'CONFIRMATION'].includes(step)" render="component"
      :device="confirmationDevice" />

    <div class="next-steps" v-else>
      <template v-if="['ENTRY', 'COMPLETE'].includes(step)">
        <template v-if="step === 'ENTRY'">
          <div class="next-steps-title">
            Pagamento da entrada
          </div>

          <div class="next-steps-text">
            Agora só falta pagar o valor da entrada para finalizar sua compra.
          </div>

          <div class="card-entrance-payment">
            <option-card title="Pagamento da entrada" :details="entrancePaymentDetails" icon="money-circle-blue-small"
              :statusIcon="entrancePaymentIcon" :action="entrancePaymentAction"
              :noAction="downPaymentPaid || !hasDownPaymentBillet" :selected="drawerEntrance"
              :disabled="!hasDownPaymentBillet" />
          </div>
        </template>

        <template v-if="step === 'COMPLETE'">
          <asset-v3 class="hide-on-large" dir="logos" name="logo.svg" />

          <div class="next-steps-title complete">
            <CheckCircleIcon size="24" color="var(--green-500)" />
            Pedido confirmado
          </div>

          <p class="next-steps-thanks">
            Obrigado por comprar com o Parcelex.
            <br />
            <br />
            Já está tudo certo com o seu pedido, basta acompanhar suas próximas parcelas.
          </p>

          <Button class="next-steps--title__complete--button primary only-on-large"
            @click="() => $router.push('/portal')">
            Ver minhas parcelas
          </Button>
        </template>
      </template>

      <template v-else>
        <div class="next-steps-title">
          Próximas etapas
        </div>
        <div class="next-steps-text">
          Conclua as etapas abaixo para finalizar sua compra.
        </div>
        <div class="card-send-documents">
          <option-card :title="bypassDocuments ? 'Envio da selfie' : 'Envio de documentos'" :details="documentDetails"
            icon="document-small" :action="documentAction" :noAction="documentSended" :selected="drawerDocument">
            <template v-if="documentSended" v-slot:hero-icon-status>
              <div class="icon-wrapper disabled">
                <ClockIcon size="20" color="var(--gray-400)" />
              </div>
            </template>
          </option-card>
        </div>
        <div class="card-entrance-payment" v-if="downPayment && step !== 'ENTRY'">
          <option-card title="Pagamento da entrada" :details="entrancePaymentText" :noAction="true" :selected="false"
            :disabled="true">
            <template v-slot:hero-icon-title>
              <CurrencyDollarIcon size="24" color="var(--gray-500)" />
            </template>

            <template v-slot:hero-icon-status>
              <div class="icon-wrapper disabled">
                <ClockIcon size="20" color="var(--gray-400)" />
              </div>
            </template>
          </option-card>
        </div>
        <div class="card-entrance-payment" v-if="step === 'ENTRY'">
          <option-card title="Pagamento da entrada" :details="entrancePaymentDetails" icon="money-circle-blue-small"
            :statusIcon="entrancePaymentIcon" :action="entrancePaymentAction" :noAction="downPaymentPaid"
            :selected="drawerEntrance" />
        </div>
      </template>

    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';

import { CurrencyDollarIcon } from '@vue-hero-icons/outline';
import { ClockIcon, CheckCircleIcon } from '@vue-hero-icons/solid';

import AssetV3 from '@/components/common/AssetV3.vue';
import Button from '@/components/ui/AppButton.vue';

import OptionCard from './OptionCard.vue';
import OnboardingConfirmation from './OnboardingConfirmation.vue';

export default {
  components: {
    AssetV3,
    Button,
    OptionCard,
    ClockIcon,
    CurrencyDollarIcon,
    CheckCircleIcon,
    OnboardingConfirmation,
  },
  props: {
    email: {
      type: String,
      default: '',
    },
    downPayment: {
      type: Boolean,
      default: true,
    },
    hasDownPaymentBillet: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('onboarding', [
      'drawerEntrance',
      'drawerDocument',
      'downPaymentPayDay',
      'downPaymentPaid',
      'documentSended',
      'source',
      'step',
      'confirmationDevice',
      'bypassDocuments',
      'chosenConfirmationResendType',
      'confirmationResendType',
      'showResendOptions',
    ]),
    entrancePaymentDetails() {
      return this.downPaymentPaid && this.downPaymentPayDay
        ? `Confirmado em ${this.downPaymentPayDay}`
        : 'Realize o pagamento da entrada para confirmar sua compra';
    },
    entrancePaymentIcon() {
      return this.downPaymentPaid
        ? 'check-circle-blue-small' : '';
    },
    entrancePaymentAction() {
      return this.downPaymentPaid ? () => { } : this.toggleDrawerEntrance;
    },
    documentDetails() {
      const sendedText = this.bypassDocuments ? 'Aguarde a aprovação da selfie enviada' : 'Aguarde a aprovação da documentação enviada';
      return this.documentSended
        ? sendedText
        : 'Para sua primeira compra, confirme sua identidade';
    },
    entrancePaymentText() {
      if (!this.bypassDocuments) return 'O pagamento será habilitado somente após aprovação do documento enviado';
      return 'O pagamento será habilitado somente após aprovação da selfie';
    },
    documentAction() {
      return this.documentSended ? () => { } : this.toggleDrawerDocument;
    },
  },
  methods: {
    ...mapMutations('onboarding', ['setDrawerEntrance', 'setDrawerDocument']),
    toggleDrawerEntrance() {
      this.setDrawerEntrance(true);
    },
    toggleDrawerDocument() {
      this.setDrawerDocument(true);
    },
  },
};
</script>

<style lang="scss" scoped>
.confirmation-options {
  margin-top: 30px;
}

.notification-checkout {
  margin-top: 25px;
  border-bottom: 1px solid #DFE3E8;
  padding-bottom: 16px;
}

.notification-checkout div:first-child {
  display: flex;
  align-items: center;
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
}

.notification-checkout div:last-child {
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 20px;
  color: #212B36;
  margin-top: 8px;
}

.next-steps {
  margin-top: 16px;
}

.next-steps .next-steps-title {
  font-weight: bold;
  font-size: 20px;
  line-height: 24px;
  color: #212B36;

  &.complete {
    display: flex;
    gap: 8px;

    svg {
      margin: 0;
    }
  }
}

.next-steps--title__complete--button {
  font-weight: 600 !important;
  margin-top: 20px;
}

.next-steps-thanks {
  margin-top: 20px;
  font-size: 16px;
  color: var(--gray-900)
}

.next-steps .next-steps-text {
  font-size: 16px;
  line-height: 24px;
  color: #637381;
  margin-top: 8px;
  margin-bottom: 4px;
}

.next-steps .card-entrance-payment,
.next-steps .card-send-documents {
  margin-top: 16px;
}

.confirmation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 24px;
}

@media (max-width: $sm) {

  /* .confirmation-options {
      margin-right: unset;
      margin-top: unset;
    } */
  .notification-checkout {
    padding-left: 24px;
    padding-right: 24px;
    border-bottom: 8px solid #F4F6F8;
  }

  .next-steps {
    margin-left: 24px;
    margin-right: 24px;
  }

  .next-steps-thanks {
    font-size: 20px;
    line-height: 28px;
  }

  .next-steps .next-steps-title {
    &.complete {
      margin-top: 20px;
    }
  }

  .logo {
    margin-top: 20px;
  }
}

@media (min-width: $md) and (max-width: $lg) {
  .confirmation-options {
    margin-top: 50px;
    margin-left: 19px;
  }
}

.alert-success {
  background-color: var(--green-50);
  padding: 20px 18px;
  display: flex;
  gap: 6px;
  line-height: 24px;
  border-radius: 6px;
  margin-bottom: 10px;
  margin-top: 20px;

  svg {
    margin: 0;
  }

  h3 {
    font-size: 16px;
    color: var(--green-600);
  }

  p {
    font-size: 14px;
    color: var(--gray-500);
  }
}
</style>
<style lang="scss">
.notification-checkout div:first-child img {
  width: 19px;
  margin-right: 10px;
}

@media (max-width: $sm) {
  .confirmation-options>img {
    margin-left: 24px;
  }
}
</style>
